import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

function isDevHostname(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host.startsWith('dev.');
}

function formatRelative(buildIso: string, nowMs: number): string {
  const buildMs = Date.parse(buildIso);
  if (Number.isNaN(buildMs)) return 'unknown';
  const diffSec = Math.max(0, Math.floor((nowMs - buildMs) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

export default function LastUpdatedBadge() {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  if (!isDevHostname()) return null;
  if (typeof __BUILD_TIME__ === 'undefined') return null;

  const rel = formatRelative(__BUILD_TIME__, now);
  const absLabel = new Date(__BUILD_TIME__).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  });

  return (
    <span
      className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200"
      title={`Build deployed ${absLabel}`}
      aria-label={`Site last updated ${rel}`}
    >
      <Clock className="w-3.5 h-3.5" aria-hidden="true" />
      <span className="whitespace-nowrap">Updated {rel}</span>
    </span>
  );
}
